import { useMemo, useState } from 'react';
import { AlertCircle, Send, User, CheckCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Textarea } from '@/components/ui/textarea.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import { Label } from '@/components/ui/label.js';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select.js';
import { useServices } from '@/contexts/ServicesContext.js';
import { useAskUserStore, type AskUserField } from '@/stores/askUserStore.js';
import { useApprovalStore } from '@/stores/approvalStore.js';
import type { ElicitationMetadata } from '@/lib/events/types.js';

interface AskUserCardProps {
  requestId?: string; // Legacy support
  approvalId?: string; // New unified approach
  elicitation?: ElicitationMetadata;
  sessionId?: string;
}

export function AskUserCard({ requestId, approvalId, elicitation }: AskUserCardProps) {
  const { streamingService } = useServices();

  // Try new unified approach first (elicitation via approval store)
  const approval = useApprovalStore((state) =>
    approvalId ? state.approvals.get(approvalId) : undefined
  );
  const updateApprovalStatus = useApprovalStore((state) => state.updateApprovalStatus);

  // Fall back to legacy request store
  const legacyRequest = useAskUserStore((state) =>
    requestId ? state.requests.get(requestId) : undefined
  );

  const setStatus = useAskUserStore((state) => state.setStatus);
  const setResponses = useAskUserStore((state) => state.setResponses);

  const [submitting, setSubmitting] = useState(false);

  // Determine which data source to use
  const useUnified = approvalId && (elicitation || approval?.metadata);
  const elicitationData = elicitation || (approval?.metadata as unknown as ElicitationMetadata);
  const request = legacyRequest;

  // Initialize values based on schema or legacy fields
  const [values, setValues] = useState<Record<string, any>>(() => {
    const initial: Record<string, any> = {};

    // Unified approach: Use elicitation schema
    if (useUnified && elicitationData?.schema) {
      const schema = elicitationData.schema as any;
      const props = schema.properties as Record<string, any>;
      if (props) {
        Object.entries(props).forEach(([key, prop]) => {
          if (prop.default !== undefined) initial[key] = prop.default;
          else if (prop.type === 'boolean') initial[key] = false;
          else if (prop.type === 'array') initial[key] = [];
          else if (prop.type === 'object') initial[key] = {};
          else initial[key] = '';
        });
      }
    }
    // Legacy approach
    else if (request) {
      // Handle legacy fields array
      if (request.fields && request.fields.length > 0) {
        request.fields.forEach((field) => {
          if (field.initialValue !== undefined) {
            initial[field.id] = field.initialValue;
          } else if (field.type === 'boolean') {
            initial[field.id] = false;
          } else {
            initial[field.id] = '';
          }
        });
      }
      // Handle JSON Schema in legacy request
      else if (request.schema && request.schema.properties) {
        const props = request.schema.properties as Record<string, any>;
        Object.entries(props).forEach(([key, prop]) => {
          if (prop.default !== undefined) initial[key] = prop.default;
          else if (prop.type === 'boolean') initial[key] = false;
          else if (prop.type === 'array') initial[key] = [];
          else if (prop.type === 'object') initial[key] = {};
          else initial[key] = '';
        });
      }
    }

    return initial;
  });

  const isDisabled = useMemo(() => {
    if (useUnified) {
      return approval?.status !== 'pending';
    }
    if (!request) return true;
    return request.status === 'submitted' || request.status === 'resolved' || request.status === 'cancelled';
  }, [useUnified, approval, request]);

  // Don't render if no data available
  if (!useUnified && !request) {
    return null;
  }

  if (useUnified && !elicitationData) {
    console.error('[AskUserCard] Elicitation data missing for approval:', approvalId);
    return null;
  }

  const handleChange = (id: string, value: any) => {
    setValues((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      if (useUnified && approvalId) {
        // New unified approach: Send approval response with form data via REST API
        await streamingService.sendAskUserResponse(
          approvalId,
          values
        );
        // Update local state for visual feedback
        updateApprovalStatus(approvalId, 'approved');
      } else if (request) {
        // Legacy approach
        await streamingService.sendAskUserResponse(request.requestId, values);
        setResponses(request.requestId, values);
        setStatus(request.requestId, 'submitted');
      }
    } catch (error) {
      console.error('Failed to send elicitation/ask_user response:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    try {
      setSubmitting(true);

      if (useUnified && approvalId) {
        // Send rejection response via REST API
        await streamingService.rejectRequest(approvalId);
        // Update local state for visual feedback
        updateApprovalStatus(approvalId, 'rejected');
      } else if (request) {
        // Legacy approach - reject via API then mark locally
        await streamingService.rejectRequest(request.requestId);
        setStatus(request.requestId, 'cancelled');
      }
    } catch (error) {
      console.error('Failed to send elicitation/ask_user decline:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Helper to render inputs from JSON Schema properties
  const renderSchemaField = (key: string, prop: any, _required: boolean) => {
    const errorMsg = ''; // Validation not yet implemented
    const baseInputClassName = `w-full ${errorMsg ? 'border-destructive focus-visible:ring-destructive' : ''}`;

    // Enum select
    if (prop.enum && Array.isArray(prop.enum)) {
        const isEnumBoolean = prop.enum.every((v: any) => typeof v === 'boolean');
        const isEnumNumeric = prop.enum.every((v: any) => typeof v === 'number');
        return (
            <Select
                value={String(values[key] || '')}
                onValueChange={(value) => {
                    let parsedValue: string | number | boolean = value;
                    if (isEnumBoolean) parsedValue = value === 'true';
                    else if (isEnumNumeric) parsedValue = Number(value);
                    handleChange(key, parsedValue);
                }}
                disabled={isDisabled}
            >
                <SelectTrigger id={key} className={baseInputClassName}>
                    <SelectValue placeholder={`Select ${prop.description || key}...`} />
                </SelectTrigger>
                <SelectContent>
                    {prop.enum.map((enumValue: any) => (
                        <SelectItem key={String(enumValue)} value={String(enumValue)}>
                            {String(enumValue)}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    }

    // Boolean checkbox
    if (prop.type === 'boolean') {
        return (
            <div className="flex items-center space-x-2">
                <Checkbox
                    id={key}
                    checked={Boolean(values[key])}
                    onCheckedChange={(checked) => handleChange(key, checked)}
                    disabled={isDisabled}
                />
                <Label htmlFor={key} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    {prop.description || key}
                </Label>
            </div>
        );
    }

    // String/Number input
    let inputFieldType = 'text';
    if (prop.type === 'number' || prop.type === 'integer') inputFieldType = 'number';
    
    // Use textarea for long text if specified or heuristically
    if (prop.type === 'string' && (prop.maxLength > 100 || prop.format === 'textarea')) {
         return (
            <Textarea
                id={key}
                value={values[key] || ''}
                onChange={(e) => handleChange(key, e.target.value)}
                className={baseInputClassName}
                placeholder={prop.description || `Enter ${key}`}
                disabled={isDisabled}
                rows={3}
            />
        );
    }

    return (
        <Input
            type={inputFieldType}
            id={key}
            value={values[key] || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            className={baseInputClassName}
            placeholder={prop.description || `Enter ${key}`}
            disabled={isDisabled}
        />
    );
  };

  // Legacy render helper
  const renderLegacyField = (field: AskUserField) => {
    const commonProps = {
      id: field.id,
      disabled: isDisabled,
      required: field.required,
      placeholder: field.placeholder,
    };

    switch (field.type) {
      case 'textarea':
        return (
          <Textarea
            {...commonProps}
            rows={3}
            value={values[field.id] as string}
            onChange={(e) => handleChange(field.id, e.target.value)}
          />
        );
      case 'select':
        return (
          <Select
            value={String(values[field.id])}
            onValueChange={(value) => handleChange(field.id, value)}
            disabled={isDisabled}
          >
            <SelectTrigger>
                <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
                {field.options?.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                    </SelectItem>
                ))}
            </SelectContent>
          </Select>
        );
      case 'boolean':
        return (
          <div className="flex items-center gap-2">
            <Checkbox
              id={field.id}
              checked={Boolean(values[field.id])}
              disabled={isDisabled}
              onCheckedChange={(checked) => handleChange(field.id, checked)}
            />
            <label htmlFor={field.id} className="text-sm text-foreground">
              {field.label}
            </label>
          </div>
        );
      case 'number':
        return (
          <Input
            {...commonProps}
            type="number"
            value={values[field.id] as number}
            onChange={(e) => handleChange(field.id, e.target.value === '' ? '' : Number(e.target.value))}
          />
        );
      default:
        return (
          <Input
            {...commonProps}
            value={values[field.id] as string}
            onChange={(e) => handleChange(field.id, e.target.value)}
          />
        );
    }
  };

  // Determine which schema and metadata to use
  const schema = useUnified
    ? (elicitationData!.schema as any)
    : request?.schema;
  const prompt = useUnified
    ? elicitationData!.prompt
    : request?.prompt;
  const title = useUnified
    ? undefined
    : request?.title;
  const fromAgent = useUnified
    ? elicitationData!.serverName
    : request?.fromAgent;

  return (
    <div
      className={cn(
        'rounded-xl border-2 transition-all bg-background/60',
        isDisabled ? 'border-muted' : 'border-primary/50'
      )}
    >
        <div className="flex items-start gap-4 px-5 py-4">
          <div className="flex-shrink-0 mt-1">
            {isDisabled ? (
              <CheckCircle className="h-6 w-6 text-muted-foreground" />
            ) : (
              <User className="h-6 w-6 text-primary" />
            )}
          </div>

          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-semibold text-foreground uppercase tracking-wide">
                User input needed
              </span>
            </div>
            <div>
              {title && <p className="text-base font-semibold mb-1">{title}</p>}
              <p className="text-sm text-foreground/80 whitespace-pre-wrap">{prompt}</p>
              {fromAgent && (
                <p className="text-xs text-muted-foreground mt-1">From: {fromAgent}</p>
              )}
            </div>

            <div className="space-y-3">
              {/* Render based on Schema if available */}
              {schema && schema.properties ? (
                  Object.entries(schema.properties as Record<string, any>).map(([key, prop]) => {
                      const required = (schema.required as string[])?.includes(key);
                      return (
                        <div key={key} className="space-y-1">
                            {prop.type !== 'boolean' && (
                                <Label htmlFor={key}>
                                    {prop.description || key}
                                    {required && <span className="text-destructive ml-1">*</span>}
                                </Label>
                            )}
                            {renderSchemaField(key, prop, required)}
                        </div>
                      );
                  })
              ) : (
                  /* Fallback to legacy fields */
                  request?.fields?.map((field) => (
                    <div key={field.id} className="space-y-1">
                      {field.type !== 'boolean' && (
                        <label htmlFor={field.id} className="text-sm font-medium text-foreground">
                          {field.label}
                          {field.required && <span className="text-destructive ml-1">*</span>}
                        </label>
                      )}
                      {renderLegacyField(field)}
                      {field.helperText && (
                        <p className="text-xs text-muted-foreground">{field.helperText}</p>
                      )}
                    </div>
                  ))
              )}
            </div>

            {/* Action buttons or status message */}
            {isDisabled ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="h-4 w-4" />
                <span>
                  {approval?.status === 'approved' ? 'Response submitted' : 'Declined'}
                </span>
              </div>
            ) : (
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={handleDecline}
                  disabled={submitting}
                  className="gap-2"
                >
                  <X className="h-4 w-4" />
                  Decline
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="gap-2"
                >
                  <Send className="h-4 w-4" />
                  Submit
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
  );
}
